import { useId, type FormEvent } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { NumberInput } from '../../ui/NumberInput';
import { Spinner } from '../../../ui/Spinner';
import { btnGhost, btnPrimary, btnSecondary, inputCls } from '../../ui/styles';
import { money } from '../../../core/format/currency';
import { today } from '../../../core/format/dates';
import { locationLabel } from '../../../core/rules/stockMovements';
import { stockLocations, type PurchaseReceipt, type PurchaseUse, type StockItem, type StockLocation } from '../../../core/types/stock';
import { useReceiptForm } from './useReceiptForm';
import { ReceiptFileField } from './ReceiptFileField';
type Props = {
  items: readonly StockItem[];
  suppliers: readonly string[];
  onDone: (receipt: PurchaseReceipt) => void;
  onCancel: () => void;
};
const fieldError = (msg: string | undefined) => msg ? <p role="alert" className="mt-1 text-[12px] text-clay" data-id="element-4813" data-mp="1fqtid1czuah7">
      {msg}
    </p> : null;

/** Een inkoopfactuur boeken: bijlage, kop en regels; na "Boeken" staat alles in één keer in de voorraad. */
export function ReceiptForm({
  items,
  suppliers,
  onDone,
  onCancel
}: Props) {
  const id = useId();
  const f = useReceiptForm(items);
  const retail = items.filter(i => i.kind === 'retail');
  const consumables = items.filter(i => i.kind === 'verbruik');
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const receipt = await f.save();
    if (!receipt) return;
    toast.success(`${f.pieces} ${f.pieces === 1 ? 'stuk' : 'stuks'} in de voorraad`, {
      description: `Factuur ${receipt.invoiceNumber} van ${receipt.supplier} is geboekt.`
    });
    onDone(receipt);
  };
  return <form onSubmit={e => void save(e)} noValidate className="mb-6 space-y-5 rounded-lg border border-line bg-white p-4 sm:p-5" data-id="element-4814" data-mp="2y6qj7mx59fxn">
      <div data-id="element-4815" data-mp="3lvji9s92c3wh">
        <h2 className="text-[15px] font-medium text-ink" data-id="element-4816" data-mp="3lvt18vvoxaso">Inkoopfactuur boeken</h2>
        <p className="mt-1 text-[13px] text-muted" data-id="element-4817" data-mp="3qocneh8g1e93">Wat er binnenkwam gaat in één keer in de voorraad, met de inkoopprijs. De factuur zelf bewaren we erbij.</p>
      </div>

      <ReceiptFileField file={f.file} preview={f.preview} error={f.fileError ?? f.show('file') ?? null} onChange={f.setFile} data-id="element-4818" data-mp="3vkgfc651nckf" />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-4819" data-mp="3qk2gnepc1ez2">
        <label htmlFor={`${id}-supplier`} className="block" data-id="element-4820" data-mp="3lm9bmozrmr0d">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4821" data-mp="3llr6t3z1f4gu">Leverancier</span>
          <input id={`${id}-supplier`} list={`${id}-suppliers`} value={f.supplier} onChange={e => f.setSupplier(e.target.value)} maxLength={80} autoComplete="off" className={inputCls} data-id="element-4822" data-mp="3hl78hx2q9ut4" />
          <datalist id={`${id}-suppliers`} data-id="element-4823" data-mp="37vnqsuqr46zu">
            {suppliers.map(s => <option key={s} value={s} data-id="element-4824" data-mp="32w1b8engaenv" />)}
          </datalist>
          {fieldError(f.show('supplier'))}
        </label>
        <label htmlFor={`${id}-number`} className="block" data-id="element-4825" data-mp="2xvwviqud9nyp">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4826" data-mp="2tubb8kfi67eh">Factuurnummer</span>
          <input id={`${id}-number`} value={f.invoiceNumber} onChange={e => f.setInvoiceNumber(e.target.value)} maxLength={60} autoComplete="off" className={inputCls} data-id="element-4827" data-mp="19csno2nluf32" />
          {fieldError(f.show('invoiceNumber'))}
        </label>
        <label htmlFor={`${id}-date`} className="block" data-id="element-4828" data-mp="1ieifh9lefg1d">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4829" data-mp="1ncjxdr7399b9">Factuurdatum</span>
          <input id={`${id}-date`} type="date" value={f.invoiceDate} max={today()} onChange={e => f.setInvoiceDate(e.target.value)} className={inputCls} data-id="element-4830" data-mp="1nah8xbll3o8n" />
          {fieldError(f.show('invoiceDate'))}
        </label>
        <label htmlFor={`${id}-location`} className="block" data-id="element-4831" data-mp="zmzdn634280x">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4832" data-mp="zlejviomet44">Komt op de plek</span>
          <select id={`${id}-location`} value={f.location} onChange={e => f.setLocation(e.target.value as StockLocation)} className={inputCls} data-id="element-4833" data-mp="14er2nc6mc5oy">
            {stockLocations.map(l => <option key={l} value={l} data-id="element-4834" data-mp="bwd4tcla8jpv">
                {locationLabel[l]}
              </option>)}
          </select>
        </label>
      </div>

      <fieldset data-id="element-4835" data-mp="2dk80ifhar04t">
        <legend className="mb-2 text-[13px] font-medium text-ink" data-id="element-4836" data-mp="29l8sgm09qn95">Regels</legend>
        <div className="hidden grid-cols-[minmax(0,1fr)_88px_120px_96px_40px] gap-x-3 pb-1 text-[12px] text-muted sm:grid" aria-hidden="true" data-id="element-4837" data-mp="2ncdnfasoyrd5">
          <span data-id="element-4838" data-mp="2ikm5tprijvz5">Artikel</span>
          <span className="text-right" data-id="element-4839" data-mp="2ikcg83adrqtz">Aantal</span>
          <span className="text-right" data-id="element-4840" data-mp="2xa1rwsg3nd8i">Prijs per stuk (€)</span>
          <span className="text-right" data-id="element-4841" data-mp="2xcec7c9bxzoq">Regel</span>
          <span data-id="element-4842" data-mp="2saf8205rmyqq" />
        </div>
        <ul className="space-y-2" data-id="element-4843" data-mp="2sb6zcysbyab7">
          {f.lines.map((l, i) => <li key={l.key} data-id="element-4844" data-mp="369ye8nf0o00e" data-mpi={i}>
              <div className="grid grid-cols-[minmax(0,1fr)_40px] items-center gap-x-3 gap-y-2 sm:grid-cols-[minmax(0,1fr)_88px_120px_96px_40px]" data-id="element-4845" data-mp="3695e45akc8r8">
                <label className="col-span-1 block" data-id="element-4846" data-mp="1bwf9asinpf52">
                  <span className="sr-only" data-id="element-4847" data-mp="1bzb8sla5kbvm">Artikel, regel {i + 1}</span>
                  <select value={l.itemId} onChange={e => f.setLine(l.key, {
                itemId: e.target.value
              })} className={inputCls} data-id="element-4848" data-mp="16zou07eoenkp">
                    <option value="" data-id="element-4849" data-mp="3intnpno5m4u8">Kies een artikel…</option>
                    <optgroup label="Producten" data-id="element-4850" data-mp="3nj91kfm5w0qn">
                      {retail.map(it => <option key={it.id} value={it.id} data-id="element-4851" data-mp="39sdyjjycgduz">
                          {it.brand ? `${it.brand} · ` : ''}
                          {it.name}
                        </option>)}
                    </optgroup>
                    {consumables.length > 0 && <optgroup label="Verbruik" data-id="element-4852" data-mp="34v3yladbwjsw">
                        {consumables.map(it => <option key={it.id} value={it.id} data-id="element-4853" data-mp="2qvmj5llxaiw7">
                            {it.name}
                          </option>)}
                      </optgroup>}
                  </select>
                </label>
                <button type="button" onClick={() => f.removeLine(l.key)} aria-label={`Regel ${i + 1} weghalen`} className="flex h-10 w-10 items-center justify-center rounded-md text-muted transition-colors duration-150 ease-soft hover:bg-sand hover:text-ink sm:order-last" data-id="element-4854" data-mp="t49soh720m77">
                  <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4855" data-mp="1t2wbn9ojtjtc" />
                </button>
                <div className="col-span-2 grid grid-cols-3 items-center gap-3 sm:col-span-1 sm:contents" data-id="element-4856" data-mp="1o41wchpsiddz">
                  <label className="block" data-id="element-4857" data-mp="2gqctfie6tw61">
                    <span className="mb-1 block text-[11px] text-muted sm:sr-only" data-id="element-4858" data-mp="2grnxljbjp5xo">Aantal</span>
                    <NumberInput allowEmpty value={l.qty} onChange={v => f.setLine(l.key, {
                  qty: v
                })} min={1} className="text-right" data-id="element-4859" data-mp="2bymipbbc96e3" />
                  </label>
                  <label className="block" data-id="element-4860" data-mp="34fp1ug1oy07a">
                    <span className="mb-1 block text-[11px] text-muted sm:sr-only" data-id="element-4861" data-mp="34gqm9b4ljz1o">Prijs per stuk (€)</span>
                    <NumberInput decimals allowEmpty value={l.cost} onChange={v => f.setLine(l.key, {
                  cost: v
                })} placeholder="excl. btw" className="text-right" data-id="element-4862" data-mp="2znnvtbv495pp" />
                  </label>
                  <span className="self-end pb-2 text-right text-[14px] tabular-nums text-ink sm:self-center sm:pb-0" data-id="element-4863" data-mp="3c09hcfr47iqm">
                    {l.qty && l.cost !== undefined ? money(Math.round(l.cost * 100) * l.qty) : '—'}
                  </span>
                </div>
              </div>
              <label className="mt-1.5 flex items-center gap-2 text-[12px] text-muted" data-id="element-4864" data-mp="3py7s7b688x1d">
                <span data-id="element-4865" data-mp="3uxkdvsld3y2d">Voor</span>
                <select value={l.use} onChange={e => f.setLine(l.key, {
              use: e.target.value as PurchaseUse
            })} aria-label={`Waar regel ${i + 1} voor is`} className="h-8 rounded-md border border-line bg-white px-2 text-[12px] text-ink" data-id="element-4866" data-mp="3uwrexdsnacus">
                  <option value="winkel" data-id="element-4867" data-mp="cii64snjvm2n">de winkel</option>
                  <option value="salon" data-id="element-4868" data-mp="hk7jiey8wbtw">salongebruik (telt niet in de winkelmarge)</option>
                  <option value="sample" data-id="element-4869" data-mp="12tcqmhfcrrur">gratis sample of try-me size</option>
                </select>
              </label>
              {fieldError(f.show(`lines.${i}`))}
            </li>)}
        </ul>
        {fieldError(f.show('lines'))}
        <button type="button" onClick={f.addLine} className={`${btnGhost} mt-2`} data-id="element-4870" data-mp="1cq7ocy3sxo7z">
          <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4871" data-mp="17rebn7wca18v" />
          Regel toevoegen
        </button>
      </fieldset>

      <div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end" data-id="element-4872" data-mp="f5bq9vsbvhqf">
        <div className="grid gap-3 sm:grid-cols-3" data-id="element-4873" data-mp="tty41t2b41fj">
          <label className="block" data-id="element-4874" data-mp="tvsrbe11w5ms">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4875" data-mp="p2g1x1ojrusz">Factuurbedrag excl. btw (ter controle)</span>
            <NumberInput decimals allowEmpty value={f.invoiceTotal} onChange={f.setInvoiceTotal} className="w-40" data-id="element-4876" data-mp="1clq8nfyscab" />
          </label>
          <label className="block" data-id="element-4877" data-mp="3sj6n4nspaef9">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4878" data-mp="281nsxn6arnuv">Btw op de factuur (voor de boekhouder)</span>
            <NumberInput decimals allowEmpty value={f.invoiceVat} onChange={f.setInvoiceVat} className="w-40" data-id="element-4879" data-mp="2cs47jalutfit" />
          </label>
          <label htmlFor={`${id}-note`} className="block" data-id="element-4880" data-mp="1y4j2hb5f5689">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4881" data-mp="232koa3u8bjnf">Notitie (optioneel)</span>
            <input id={`${id}-note`} value={f.note} onChange={e => f.setNote(e.target.value)} maxLength={200} className={inputCls} data-id="element-4882" data-mp="23089fzjrppel" />
          </label>
        </div>
        <dl className="text-right text-[14px]" data-id="element-4883" data-mp="2vqq7un9jr2lo">
          <div className="flex justify-end gap-3" data-id="element-4884" data-mp="2hrqsk67xc026">
            <dt className="text-muted" data-id="element-4885" data-mp="2hqxmz8kpbglm">{f.pieces} stuks, totaal</dt>
            <dd className="tabular-nums text-ink" data-id="element-4886" data-mp="2lsaqhc0bn7pd">{money(f.totalCents)}</dd>
          </div>
          {f.mismatch !== null && <div className={`mt-1 text-[13px] ${f.mismatch === 0 ? 'text-sage' : 'text-clay'}`} aria-live="polite" data-id="element-4887" data-mp="16aqu0suzgozp">
              {f.mismatch === 0 ? 'Klopt met de factuur' : `${money(Math.abs(f.mismatch))} ${f.mismatch > 0 ? 'meer' : 'minder'} op de factuur dan in de regels`}
            </div>}
        </dl>
      </div>

      {f.error && <p role="alert" className="text-[13px] text-clay" data-id="element-4888" data-mp="2wah39r2kppht">
          {f.error}
        </p>}

      <div className="flex flex-wrap gap-2" data-id="element-4889" data-mp="30b0yuq7i0l1u">
        <button type="submit" disabled={f.submitting} aria-busy={f.submitting} className={btnPrimary} data-id="element-4890" data-mp="309zkabr9xgfd">
          {f.submitting && <Spinner data-id="element-4891" data-mp="2me4aqwirlhon" />}
          {f.pieces > 0 ? `${f.pieces} ${f.pieces === 1 ? 'stuk' : 'stuks'} boeken op ${locationLabel[f.location].toLowerCase()}` : 'Boeken'}
        </button>
        <button type="button" onClick={onCancel} disabled={f.submitting} className={btnSecondary} data-id="element-4892" data-mp="2dcwcgeg90guk">
          Annuleren
        </button>
      </div>
    </form>;
}